import type { HTMLReactParserOptions } from 'html-react-parser';
import Parser, { DOMNode, domToReact, Element } from 'html-react-parser';
import type { NodeWithChildren, Text } from 'domhandler';
import * as React from 'react';
import { Link } from 'react-router-dom';

import './Block.scss';
import { BlockTableReactTable, BlockTableReactTableProps } from './BlockTable';
import { createColumnHelper, GroupColumnDef } from '@tanstack/react-table';
import { AccessorKeyColumnDef } from '@tanstack/table-core/build/lib/types';
import { ColumnDef } from '@tanstack/table-core/src/types';
import { LEARN_PART_PATH_OPERATORS } from '../../../shared/constants';

export const getIsInternalLink = (link: string): boolean =>
  !(link.startsWith('https://') || link.startsWith('http://') || link.startsWith('mailto:'));

const parseTextAlignClassNames = (classNames: string[]): { className: string; headerClassName: string } => {
  const textAlignClassNameRegex = /^(cell:)?align-(left|center|right|justify|start|end)$/;
  const textAlignClassNames = classNames.filter(className => textAlignClassNameRegex.test(className));

  return {
    headerClassName: textAlignClassNames.filter(className => !className.startsWith('cell:')).join(' '),
    className: textAlignClassNames
      .filter(className => className.startsWith('cell:'))
      .map(className => className.slice(5))
      .join(' '),
  };
};

interface MakeHeaderParams {
  header: any;
  className?: string;
}

const parseTableHeader = (thead: any): BlockTableReactTableProps['columns'] => {
  const rows = thead.children.filter((e: any) => e.name === 'tr');
  if (rows.length < 0 || rows.length > 2) {
    throw Error('react-table requires header and 1 nested level');
  }

  const Cell = (props: any) => {
    const node = props.value;

    const options: HTMLReactParserOptions = {
      replace: domNode => {
        if (domNode instanceof Element && domNode.attribs) {
          if (domNode.name === 'a' && getIsInternalLink(domNode.attribs.href)) {
            return (
              <Link to={domNode.attribs.href.replace('/references/', `/${LEARN_PART_PATH_OPERATORS}/`)}>
                {domToReact(domNode.children)}
              </Link>
            );
          }
        }

        return null;
      },
    };

    return domToReact(node, options);
  };

  const makeHeader = ({ header, className }: MakeHeaderParams) => (
    <div style={{ display: 'flex' }}>
      {header}
      {/* Render sort icon */}
      <div className="sort-icon" />
    </div>
  );

  const columnHelper = createColumnHelper<Record<string, string>>();

  if (rows.length === 1) {
    return rows[0].children
      .filter((e: any) => e.name === 'th')
      .map((th: any, index: number) => {
        let headerClassName, cellClassName;
        if (th.attribs && th.attribs.class && th.attribs.class !== '') {
          const classNames: string[] = th.attribs.class.split(' ');
          ({ headerClassName, className: cellClassName } = parseTextAlignClassNames(classNames));
        }
        const Header = makeHeader({ header: th.children.length > 0 ? domToReact(th.children) : '', className: headerClassName });

        const column = columnHelper.accessor(`col-${index}`, {
          cell: (info: any) => {
            // @ts-expect-error dynamic column key not in react-table row type
            return <Cell value={info.row.original[info.column.id]} />;
          },
          accessorKey: `col-${index}`,
          header: () => Header,
          meta: {
            className: cellClassName,
          },
          // @ts-expect-error custom sortingFn string not in react-table union
          sortingFn: 'sortParsedValues',
          id: `col-${index}`,
        });

        return column;
      });
  }

  interface QueueElement {
    id: number;
    column: any;
    expectedChildCount: number;
  }

  let id = 0;
  const columns = [];
  const queue: QueueElement[] = [];

  for (let rowId = 0; rowId < 2; rowId++) {
    const cols = rows[rowId].children.filter((e: any) => e.name === 'th');
    for (let colId = 0; colId < cols.length; colId++) {
      const th = cols[colId];
      const Header = makeHeader({ header: th.children.length > 0 ? domToReact(th.children) : '' });

      // Hold column options like width, minWidth, maxWidth, ...
      const columnOptions: any = { sortingFn: 'sortParsedValues' };
      if (th.attribs && th.attribs.width) {
        const width = Number(th.attribs.width);
        if (!Number.isNaN(width)) {
          columnOptions.width = width;
        }
      }

      if (th.attribs && th.attribs.class && th.attribs.class !== '') {
        const classNames: string[] = th.attribs.class.split(' ');
        const { headerClassName, className } = parseTextAlignClassNames(classNames);
        columnOptions.className = className;
        columnOptions.headerClassName = headerClassName;
      }

      if (rowId === 0) {
        // If its rowspan equals to two, it is fixed, non-nested element
        if (th.attribs && Number(th.attribs.rowspan) === 2) {
          columns.push({
            header: () => Header,
            columns: [
              columnHelper.accessor(`col-${id}`, {
                header: () => Header,
                cell: (info: any) => {
                  // @ts-expect-error dynamic column key not in react-table row type
                  return <Cell value={info.row.original[info.column.id]} />;
                },
                id: `col-${id}`,
                ...columnOptions,
              }),
            ],
            id: id.toString(),
          });
          id++;
          continue;
        }

        if (!th.attribs || !th.attribs.colspan) {
          throw Error('first row element without rowspan must have colspan');
        }

        const column = { Header, columns: [] };
        const expectedChildCount = Number(th.attribs.colspan);
        queue.push({ column, expectedChildCount, id });
        id += expectedChildCount;
        continue;
      }

      if (queue.length === 0) {
        throw Error('invalid table');
      }

      queue[0].column.columns.push(
        columnHelper.accessor(`col-${queue[0].id + (queue[0].column.columns.length as number)}`, {
          cell: (info: any) => {
            // @ts-expect-error dynamic column key not in react-table row type
            return <Cell value={info.row.original[info.column.id]} />;
          },
          accessorKey: `col-${queue[0].id + (queue[0].column.columns.length as number)}`,
          header: () => Header,
          sortingFn: 'sortParsedValues',
          id: `col-${queue[0].id + (queue[0].column.columns.length as number)}`,
          ...columnOptions,
        })
      );

      // Full-filled expected childrens, remove it from queue
      if (queue[0].column.columns.length === queue[0].expectedChildCount) {
        const column = queue.shift();
        if (column !== undefined) {
          columns.push({ id: column.id, ...column.column });
        }
      }
    }
  }

  return columns
    .sort((a, b) => a.id - b.id)
    .map(column => {
      //delete column.id;

      return {
        id: column.id,
        header: column.Header,
        columns: column.columns,
      };
    });
};

const parseTableData = (tbody: any): BlockTableReactTableProps['data'] => {
  if (!tbody.children || tbody.children.length === 0) {
    throw Error('there are no children in tbody');
  }

  const data: any[] = [];

  const rows = tbody.children.filter((e: any) => e.name === 'tr');
  for (const row of rows) {
    if (!row.children || row.children.length === 0) {
      throw Error('there are not children in tr');
    }

    data.push(
      row.children
        .filter((e: any) => e.name === 'td')
        .map((td: any) => (td.children.length > 0 ? td.children : ''))
        .reduce((prev: any, current: any, index: number) => {
          prev[`col-${index}`] = current;

          return prev;
        }, {})
    );
  }

  return data;
};

const parseTableFooter = (tfoot: any, columns: GroupColumnDef<string, any>[]) => {
  if (columns === undefined) {
    throw Error('columns is undefined');
  }

  if (!tfoot.children || tfoot.children.length === 0) {
    throw Error('there are no children in tfoot');
  }

  const row = tfoot.children.find((e: any) => e.name === 'tr');
  if (!row.children || row.children.length === 0) {
    throw Error('there are no children in tr');
  }

  const cols = row.children.filter((e: any) => e.name === 'td');
  const queue = columns
    .map((column, id) => {
      if (column.columns === undefined) {
        return { isNested: false, id };
      }

      return column.columns.map((_, subId: number) => ({
        isNested: true,
        id,
        subId,
      }));
    })
    .flat();

  for (const col of cols) {
    const Footer = col.children.length > 0 ? domToReact(col.children) : '';

    const good = queue.shift();
    if (good === undefined) {
      throw Error('can not find a column to add footer');
    }

    if (good.isNested && 'subId' in good) {
      (columns[good.id].columns as any)[good.subId].Footer = Footer;
    } else {
      columns[good.id].footer = () => Footer;
    }
  }
};

type TableCell = number | string | DOMNode | DOMNode[] | React.ReactNode | undefined | null;

export const isTextNode = (node: TableCell): node is Text => {
  return node instanceof Object && (node as DOMNode).type === 'text' && typeof (node as Text).data === 'string';
};

const isNodeWithChildren = (node: TableCell): node is NodeWithChildren => {
  return node instanceof Object && Array.isArray((node as NodeWithChildren).children);
};

export const getData = (node: TableCell): string => {
  if (!node) return '';
  if (typeof node === 'number') return node.toString(10);
  if (typeof node === 'string') return node;
  if (isTextNode(node)) return node.data;
  if (isNodeWithChildren(node)) return getData(node.children);
  if (React.isValidElement(node)) return getData((node?.props as any)?.children);
  if (Array.isArray(node)) return node.map((el: TableCell) => getData(el)).join('');
  return '';
};

const adjustColumnsWidth = (columns: BlockTableReactTableProps['columns'], data: any) => {
  if (columns === undefined) {
    throw Error('columns is undefined');
  }

  const MAX_WIDTH = 200;
  const MIN_WIDTH = 50;
  const DEFAULT_WIDTH = 100;
  const LETTER_WIDTH = 5;

  const queue = columns
    .filter(c => c.size === undefined)
    .map((column, id) => {
      // @ts-expect-error react-table column union lacks .columns discriminator
      if (column.columns === undefined) {
        return { isNested: false, id };
      }
      // @ts-expect-error react-table column union lacks .columns discriminator
      return column.columns
        .filter((c: GroupColumnDef<string, any>) => c.size === undefined)
        .map((_: any, subId: number) => ({
          isNested: true,
          id,
          subId,
        }));
    })
    .flat();

  while (queue.length > 0) {
    const col = queue.shift();
    if (col === undefined) {
      throw Error('it should never been here');
    }

    const findMaxContentLength = (contents: string[]) => Math.max(...contents.map(c => c.length));
    if (col.isNested && 'subId' in col) {
      const currentColumn = columns[col.id] as GroupColumnDef<Record<string, string>>;
      const headerData = getData((currentColumn.columns as any)[col.subId].Header);
      const accessor = (currentColumn.columns as any)[col.subId].accessorKey;

      const colData = data.map((d: any) => getData(d[accessor]));
      if (colData.length === 0) {
        throw Error('there are no data for this column');
      }

      if (typeof headerData === 'string') {
        colData.push(headerData);
      }

      let minWidth = DEFAULT_WIDTH;
      if (typeof colData[0] === 'string') {
        minWidth = findMaxContentLength(colData) * LETTER_WIDTH;
      }
      (currentColumn.columns as ColumnDef<Record<string, string>, any>[])[col.subId].size = Math.min(
        Math.max(minWidth, MIN_WIDTH),
        MAX_WIDTH
      );
    } else {
      const currentColumn = columns[col.id] as AccessorKeyColumnDef<Record<string, string>>;
      // @ts-expect-error react-table column header type is a union
      const headerData = getData(currentColumn.header);

      const accessor = currentColumn.accessorKey;

      const colData = data.map((d: any) => getData(d[accessor]));
      if (colData.length === 0) {
        throw Error('there are no data for this column');
      }

      if (typeof headerData === 'string') {
        colData.push(headerData);
      }

      if (headerData === 'Operator') {
        columns[col.id].maxSize = 380;
      }

      let minWidth = DEFAULT_WIDTH;
      if (typeof colData[0] === 'string') {
        minWidth = findMaxContentLength(colData) * LETTER_WIDTH;
      }
      columns[col.id].size = Math.min(Math.max(minWidth, MIN_WIDTH), MAX_WIDTH);
    }
  }
};

const toReactTableProps = (table: any): BlockTableReactTableProps => {
  const thead = table.children.find((e: any) => e.name === 'thead');
  const tbody = table.children.find((e: any) => e.name === 'tbody');
  const tfoot = table.children.find((e: any) => e.name === 'tfoot');

  if (thead === undefined || tbody === undefined) {
    throw Error('react-table requires header and body');
  }

  const columns = parseTableHeader(thead);
  const data = parseTableData(tbody);

  adjustColumnsWidth(columns, data);

  // Parse footer and add up to columns definition
  if (tfoot !== undefined) {
    // @ts-expect-error parseTableFooter overload mismatch
    parseTableFooter(tfoot, columns);
  }

  return { data, columns, pageSize: data.length };
};

const internalLinkAndTableParser: HTMLReactParserOptions['replace'] = domNode => {
  let retVal = null;
  if (domNode instanceof Element && domNode.attribs && domNode.attribs.href) {
    const isInternal = getIsInternalLink(domNode.attribs.href);
    if (RegExp(/^\$/).test(domNode.attribs.href)) {
      const rewriteHref = (href: string) => {
        const elements = href.split('/');
        const serverToClientMap = new Map([
          ['references', LEARN_PART_PATH_OPERATORS],
          ['reference', LEARN_PART_PATH_OPERATORS],
          ['tutorialpage', 'documentation'],
        ]);

        if (serverToClientMap.has(elements[0].replace('$', ''))) {
          elements[0] = serverToClientMap.get(elements[0].replace('$', '')) as string;
          const location = window.location;

          return `${isInternal ? '' : `${location.protocol}//${location.host}`}/learn/${elements.join('/')}`;
        } else {
          return href;
        }
      };
      domNode.attribs.href = rewriteHref(domNode.attribs.href);
    }

    if (domNode.name === 'a' && isInternal) {
      // Replace anchor tag by Link component to prevent reload page
      retVal = (
        <Link to={domNode.attribs.href.replace('/references/', `/${LEARN_PART_PATH_OPERATORS}/`)}>{domToReact(domNode.children)}</Link>
      );
    } else {
      retVal = null;
    }

    return retVal;
  }

  if (domNode instanceof Element && domNode.name === 'table') {
    return <BlockTableReactTable {...toReactTableProps(domNode)} />;
  } else {
    retVal = null;
  }

  return retVal;
};

interface BlockTextProps {
  value: string;
}

export const BlockText = ({ value }: BlockTextProps) => (
  <div className="block-text">
    {Parser(value, {
      replace: internalLinkAndTableParser,
    })}
  </div>
);
